import { useEffect, useState } from "react";
import { Plus, Loader2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  createRunner,
  listAllRunners,
  updateRunner,
  type RunnerSummary,
} from "@/lib/api";

const HEARTBEAT_FRESH_MS = 60_000;

export function AdminRunnersPage() {
  const [runners, setRunners] = useState<RunnerSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  async function refresh() {
    try {
      setRunners(await listAllRunners());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    }
  }

  useEffect(() => {
    void refresh();
    const t = setInterval(refresh, 10_000);
    return () => clearInterval(t);
  }, []);

  function statusOf(r: RunnerSummary): "online" | "stale" | "offline" {
    if (!r.last_seen_at) return "offline";
    const ageMs = Date.now() - new Date(r.last_seen_at).getTime();
    if (ageMs < HEARTBEAT_FRESH_MS) return "online";
    if (ageMs < HEARTBEAT_FRESH_MS * 5) return "stale";
    return "offline";
  }

  return (
    <div className="container py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Runners</h1>
          <p className="text-sm text-muted-foreground">
            Machines that claim Trigger.dev runs. Each machine sets
            <code className="mx-1 rounded bg-muted px-1 py-0.5 text-xs">RUNNER_ID</code>
            and POSTs to <code className="mx-1 rounded bg-muted px-1 py-0.5 text-xs">/api/runners/heartbeat</code>.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" /> Register runner
            </Button>
          </DialogTrigger>
          <NewRunnerDialog
            onCreated={async () => {
              setOpen(false);
              await refresh();
            }}
          />
        </Dialog>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {!runners && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {runners && (
        <div className="rounded-lg border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Runner</th>
                <th className="px-4 py-2">Trust</th>
                <th className="px-4 py-2">Capabilities</th>
                <th className="px-4 py-2">Last seen</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {runners.map((r) => {
                const s = statusOf(r);
                return (
                  <tr key={r.id} className="border-b last:border-0">
                    <td className="px-4 py-3">
                      <Badge
                        variant={
                          s === "online" ? "success" : s === "stale" ? "warning" : "outline"
                        }
                      >
                        {s}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{r.name}</div>
                      <div className="font-mono text-xs text-muted-foreground">{r.id}</div>
                    </td>
                    <td className="px-4 py-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="rounded outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
                            aria-label={`Change trust level (currently ${r.trust_level})`}
                          >
                            <Badge
                              variant={
                                r.trust_level === "self"
                                  ? "default"
                                  : r.trust_level === "trusted"
                                    ? "info"
                                    : "destructive"
                              }
                              className="cursor-pointer hover:opacity-80"
                            >
                              {r.trust_level}
                            </Badge>
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuLabel>Trust level</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          {(["self", "trusted", "untrusted"] as const).map(
                            (t) => (
                              <DropdownMenuItem
                                key={t}
                                onSelect={async () => {
                                  if (t === r.trust_level) return;
                                  try {
                                    await updateRunner(r.id, { trustLevel: t });
                                    await refresh();
                                  } catch (e) {
                                    setError(
                                      e instanceof Error
                                        ? e.message
                                        : "Failed to update"
                                    );
                                  }
                                }}
                              >
                                <Check
                                  className={
                                    "mr-2 h-3.5 w-3.5 " +
                                    (t === r.trust_level
                                      ? "opacity-100"
                                      : "opacity-0")
                                  }
                                />
                                {t}
                              </DropdownMenuItem>
                            )
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {r.capabilities.length === 0 && (
                          <span className="text-xs text-muted-foreground">none</span>
                        )}
                        {r.capabilities.map((c) => (
                          <Badge key={c} variant="outline" className="font-mono text-[10px]">
                            {c}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {r.last_seen_at
                        ? new Date(r.last_seen_at).toLocaleString()
                        : "never"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          await updateRunner(r.id, { active: !r.active });
                          await refresh();
                        }}
                      >
                        {r.active ? "Deactivate" : "Activate"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function NewRunnerDialog({ onCreated }: { onCreated: () => void | Promise<void> }) {
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [capabilities, setCapabilities] = useState("ollama");
  const [trustLevel, setTrustLevel] = useState<"self" | "trusted" | "untrusted">(
    "self"
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await createRunner({
        id,
        name: name || id,
        trustLevel,
        capabilities: capabilities
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      });
      await onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create");
    } finally {
      setBusy(false);
    }
  }

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Register runner</DialogTitle>
        <DialogDescription>
          Pre-register so this admin row is here when the runner first
          heartbeats. Or just point a runner at /api/runners/heartbeat —
          it'll auto-create itself with trust=untrusted.
        </DialogDescription>
      </DialogHeader>
      <form className="space-y-4" onSubmit={submit}>
        <div className="space-y-1.5">
          <Label htmlFor="rid">ID</Label>
          <Input
            id="rid"
            placeholder="linux-server"
            value={id}
            onChange={(e) => setId(e.target.value)}
            required
            pattern="[a-z0-9-]+"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="rname">Display name</Label>
          <Input
            id="rname"
            placeholder="Linux GPU server"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="rcap">Capabilities</Label>
          <Input
            id="rcap"
            placeholder="ollama, gpu"
            value={capabilities}
            onChange={(e) => setCapabilities(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Comma-separated. Conventions: ollama, gpu, computer-use-mac,
            computer-use-linux, browser, high-mem.
          </p>
        </div>
        <div>
          <Label>Trust level</Label>
          <div className="mt-1 flex gap-1">
            {(["self", "trusted", "untrusted"] as const).map((t) => (
              <Button
                key={t}
                size="sm"
                type="button"
                variant={trustLevel === t ? "default" : "outline"}
                onClick={() => setTrustLevel(t)}
              >
                {t}
              </Button>
            ))}
          </div>
        </div>
        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}
        <Button type="submit" disabled={busy} className="w-full">
          {busy ? "Creating…" : "Register"}
        </Button>
      </form>
    </DialogContent>
  );
}
